import { useState, useEffect } from "react";
import { Box, Stack } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { useTranslate } from "src/locales";
import ByzNurseSaveButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseSaveButton";
import ByzNurseDeleteButton from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNurseDeleteButton";
import ByzNursePrintButton2 from "src/components/core-components/mui/buttons/ByzNurseButton/ByzNursePrintButton2";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";

interface CareFormData {
  id: string;
  date: Date;
  nurse: string;
  treatment: string;
  technique: string;
  treatmentMethod: string;
  specialCare: string;
  feeding: string;
  alert: string;
  pulse: string;
  breathing: string;
  temperature: string; 
  urine: string;
  stool: string;
  weight: string;
  breastMilk: string; 
  examination: string; 
  hud02: string; 
  meconium: string;
  anomaly: string;
}

interface NewbornCareFormProps {
  onSave: (formData: CareFormData) => void;
  initialData?: any;
  isEditing: boolean;
}

function NewbornCareForm({ onSave, initialData, isEditing }: NewbornCareFormProps) {
  const [alertOpen, setAlertOpen] = useState(false);
  const { t } = useTranslate();

  const [formData, setFormData] = useState<CareFormData>({
    id: Date.now().toString(),
    date: new Date(),
    nurse: "",
    treatment: "",
    technique: "",
    treatmentMethod: "",
    specialCare: "",
    feeding: "",
    alert: "",
    pulse: "",
    breathing: "",
    temperature: "",
    urine: "",
    stool: "",
    weight: "",
    breastMilk: "",
    examination: "",
    hud02: "",
    meconium: "",
    anomaly: ""
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        date: initialData.date ? new Date(initialData.date) : new Date(),
      });
    } else {
      setFormData({
        id: Date.now().toString(),
        date: new Date(),
        nurse: "",
        treatment: "",
        technique: "",
        treatmentMethod: "",
        specialCare: "",
        feeding: "",
        alert: "",
        pulse: "",
        breathing: "",
        temperature: "",
        urine: "",
        stool: "",
        weight: "",
        breastMilk: "",
        examination: "",
        hud02: "",
        meconium: "",
        anomaly: ""
      });
    }
  }, [initialData]);

  const handleChange = (field: keyof CareFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    onSave(formData);
    setAlertOpen(true);
  };

  return (
    <>
      <Stack
        sx={{ padding: "5px", marginBottom: "20px" }}
        mt={1}
        direction="row"
        spacing={2}
      >
        <ByzNurseSaveButton onClick={handleSave} />
        <ByzNurseDeleteButton />
        <ByzNursePrintButton2 />
      </Stack>

      <Box sx={{ padding: "10px", width: "100%" }}>
        <Grid container spacing={2}>
          <Grid md={6} xs={6} container direction={"column"} spacing={3}>
            <Grid
              md={6}
              xs={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle>
                {t("newborn-patient-assessment.dialog.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                md={6}
                xs={6}
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  paddingRight: "10px",
                  display: "flex",
                }}
              >
                <ByzMuiTypography pl={3} pt={0}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiDateTimePicker 
                  sx={{ width: "100%" }} 
                  value={formData.date}
                  onChange={(newDate) => handleChange("date", newDate)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.nurse")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.nurse}
                  onChange={(e) => handleChange("nurse", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.examination")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.examination}
                  onChange={(e) => handleChange("examination", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.technique")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.technique}
                  onChange={(e) => handleChange("technique", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.treatment")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.treatment}
                  onChange={(e) => handleChange("treatment", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.treatmentMethod")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.treatmentMethod}
                  onChange={(e) => handleChange("treatmentMethod", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.specialCare")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.specialCare}
                  onChange={(e) => handleChange("specialCare", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.bodycare")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.feeding}
                  onChange={(e) => handleChange("feeding", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.breastMilk")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.breastMilk}
                  onChange={(e) => handleChange("breastMilk", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.alert")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.alert}
                  onChange={(e) => handleChange("alert", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.pulse")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.pulse}
                  onChange={(e) => handleChange("pulse", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.breathing")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.breathing}
                  onChange={(e) => handleChange("breathing", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.temperature")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.temperature}
                  onChange={(e) => handleChange("temperature", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.pain")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.urine}
                  onChange={(e) => handleChange("urine", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.urine")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.stool}
                  onChange={(e) => handleChange("stool", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.stool")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.weight}
                  onChange={(e) => handleChange("weight", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.hud02")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.hud02}
                  onChange={(e) => handleChange("hud02", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.meconium")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.meconium}
                  onChange={(e) => handleChange("meconium", e.target.value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid md={6} xs={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                <ByzMuiTypography pl={3}>
                  {t("newborn-patient-assessment.dialog.anomaly")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6} sx={{ paddingTop: "0px" }}>
                <ByzMuiTextField 
                  sx={{ width: "100%" }}
                  value={formData.anomaly}
                  onChange={(e) => handleChange("anomaly", e.target.value)}
                />
              </Grid>
            </Grid>

            <ByzMuiSnackBar
              open={alertOpen}
              onClose={() => setAlertOpen(false)}
              title={t("operation-successful")}
              message={t("operation-successful-message")}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default NewbornCareForm;